import { useId } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PAGE_SIZES } from "./pagination";

interface Props {
  /** Página atual, a partir de 1. */
  page: number;
  pageSize: number;
  /** Total de itens com os filtros. */
  total: number;
  /** Total sem filtro, para mostrar "N de M"; omita quando não se aplica. */
  unfilteredTotal?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Trava a navegação enquanto a próxima página carrega. */
  disabled?: boolean;
}

/** Itens por página, "Página X de Y" e Anterior/Próxima, das listagens de grupos (na tela ou na API). */
export function TablePagination({ page, pageSize, total, unfilteredTotal, onPageChange, onPageSizeChange, disabled }: Props) {
  const pageSizeId = useId();
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
      <div className="flex items-center gap-2">
        <Label htmlFor={pageSizeId} className="text-sm text-muted-foreground">Itens por página:</Label>
        <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
          <SelectTrigger id={pageSizeId} className="w-[80px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)}>{size}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm text-muted-foreground" aria-live="polite">
        Página {Math.min(page, pageCount)} de {pageCount} · {total}
        {unfilteredTotal !== undefined && unfilteredTotal !== total ? ` de ${unfilteredTotal}` : ""} item(ns)
      </p>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={disabled || page <= 1}>
          <span className="sr-only">Página </span>Anterior
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={disabled || page >= pageCount}>
          <span className="sr-only">Página </span>Próxima
        </Button>
      </div>
    </div>
  );
}
